import { useEffect, useRef, useState } from 'react'
import { Logic } from '../logic'

export function useLogic<L extends Logic<object, object>, U extends object>(
    LogicClass: new (deps: L['deps']) => L,
    deps: L['deps'],
    /**
     * If provided, the logic class will be patched to be extended with the provided methods.
     * This is useful for extending the logic with additional methods that need to be shared by multiple logic classes.
     */
    initSharedLogicMethods?: (logic: L) => U,
    options?: {
        /**
         * Optionally select a slice of the state to drive React re-renders.
         * If omitted, the full state drives re-renders (current behavior).
         */
        select?: (state: ReturnType<L['getInitialState']>) => unknown
        /**
         * Equality comparator for selected state. Defaults to shallow reference equality.
         */
        equals?: (a: unknown, b: unknown) => boolean
    },
): {
    logic: L & U
    state: ReturnType<L['getInitialState']>
} {
    type State = ReturnType<L['getInitialState']>

    const logicRef = useRef(new LogicClass(deps))
    let logic = logicRef.current

    if (initSharedLogicMethods != null) {
        logic = Object.assign(logic, initSharedLogicMethods(logic))
    }

    let initialState = logic.getInitialState() as State
    const select = options?.select
    const equals = options?.equals ?? ((a: unknown, b: unknown) => a === b)

    // Store the full state in a ref for logic methods; React state may be a selected slice
    let [state, setState] = useState<State>(initialState)
    logic.getState = () => state
    logic.setState = (newState) => {
        const nextFullState = { ...state, ...newState }
        // Only trigger React re-render if the selected slice changed (or no selector provided)
        if (select != null) {
            const prevSelected = select(state as State)
            const nextSelected = select(nextFullState as State)
            if (!equals(prevSelected, nextSelected)) {
                state = nextFullState
                setState(nextFullState as State)
            } else {
                state = nextFullState
            }
        } else {
            state = nextFullState
            setState(nextFullState as State)
        }
    }
    useEffect(() => {
        logic.initialize?.()

        if (logic.cleanup != null) {
            return () => {
                logic.cleanup()
                for (const fn of logic.cleanupFns) {
                    fn()
                }
            }
        }
    }, [logic])

    useEffect(() => {
        const oldDeps = logic.deps
        logic.deps = deps
        if (logic.shouldReload(oldDeps, deps)) {
            logic.initialize?.()
        }
    }, [...Object.values(deps), logic])

    return { logic: logic as L & U, state }
}
